import { useTheme } from "@mui/material";
import { useMemo } from "react";
import { Assessment as AssessmentIcon, Error as ErrorIcon, Warning as WarningIcon, Info as InfoIcon, CheckCircle as CheckCircleIcon, Archive as ArchiveIcon } from "@mui/icons-material";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { calculateNextReviewDueDate } from "../../shared/hooks/useNextReviewDate";

const AccessSummary = ({ systems = [], loading = false }) => {
  const theme = useTheme();

  const stats = useMemo(() => {
    if (loading) return { total: 0, critical: 0, overdue: 0, active: 0, archived: 0, daysUntilNextReview: null };

    const total = systems.length;
    const critical = systems.filter(s => s.is_critical === true).length;

    const overdue = systems.filter(system => {
      const nextReviewDate = calculateNextReviewDueDate(
        system.last_reviewed_at,
        system.review_cycle,
        system.created_at
      );
      return nextReviewDate && nextReviewDate < new Date();
    }).length;

    const active = systems.filter(s => s.is_review_active !== false).length;
    const archived = systems.filter(s => s.is_review_active === false).length;

    const now = new Date();
    const daysUntilReviews = systems
      .filter(s => s.is_review_active !== false) // Only active systems
      .map(system => {
        const nextReviewDate = calculateNextReviewDueDate(
          system.last_reviewed_at,
          system.review_cycle,
          system.created_at
        );
        if (!nextReviewDate) return null;
        const diffTime = nextReviewDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 ? diffDays : null; // Only future dates
      })
      .filter(days => days !== null);

    const daysUntilNextReview = daysUntilReviews.length > 0 ? Math.min(...daysUntilReviews) : null;

    return { total, critical, overdue, active, archived, daysUntilNextReview };
  }, [systems, loading]);

  const displayStat = (value) => {
    if (loading) return "-";
    return value;
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Paper elevation={0} sx={{
          p: 2,
          borderRadius: "12px",
          border: "1px solid #E8EAED",
          backgroundColor: "#FFFFFF",
          flex: 1,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            transform: "translateY(-2px)",
          },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong}>
                {displayStat(stats.total)}
              </Typography>
              <Typography variant="body2" color={theme.customColors.text.secondary}>
                Total Accounts
              </Typography>
            </Box>
            <AssessmentIcon sx={{ color: theme.customColors.icons.primary, fontSize: 24 }} />
          </Box>
        </Paper>

        <Paper elevation={0} sx={{
          p: 2,
          borderRadius: "12px",
          border: "1px solid #E8EAED",
          backgroundColor: "#FFFFFF",
          flex: 1,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            transform: "translateY(-2px)",
          },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5" fontWeight="600" color="#ef4444">
                {displayStat(stats.critical)}
              </Typography>
              <Typography variant="body2" color={theme.customColors.text.secondary}>
                Critical Accounts
              </Typography>
            </Box>
            <Box sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#ef4444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <ErrorIcon sx={{ color: "#FFFFFF", fontSize: 16 }} />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{
          p: 2,
          borderRadius: "12px",
          border: "1px solid #E8EAED",
          backgroundColor: "#FFFFFF",
          flex: 1,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            transform: "translateY(-2px)",
          },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5" fontWeight="600" color="#f59e0b">
                {displayStat(stats.overdue)}
              </Typography>
              <Typography variant="body2" color={theme.customColors.text.secondary}>
                Overdue Reviews
              </Typography>
            </Box>
            <Box sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#f59e0b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <WarningIcon sx={{ color: "#FFFFFF", fontSize: 16 }} />
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{
          p: 2,
          borderRadius: "12px",
          border: "1px solid #E8EAED",
          backgroundColor: "#FFFFFF",
          flex: 1,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            transform: "translateY(-2px)",
          },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5" fontWeight="600" color={stats.daysUntilNextReview !== null && stats.daysUntilNextReview <= 7 ? "#f59e0b" : "#10b981"}>
                {loading ? "-" : stats.daysUntilNextReview !== null ? `${stats.daysUntilNextReview}` : "N/A"}
              </Typography>
              <Typography variant="body2" color={theme.customColors.text.secondary}>
                Days Until Next Review
              </Typography>
            </Box>
            <Box sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: stats.daysUntilNextReview !== null && stats.daysUntilNextReview <= 7 ? "#f59e0b" : "#10b981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              {stats.daysUntilNextReview !== null && stats.daysUntilNextReview <= 7 ? (
                <WarningIcon sx={{ color: "#FFFFFF", fontSize: 16 }} />
              ) : (
                <CheckCircleIcon sx={{ color: "#FFFFFF", fontSize: 16 }} />
              )}
            </Box>
          </Box>
        </Paper>


      </Stack>
    </Box>
  );
};

export default AccessSummary;